/**
 * Cassanova Token Manager
 *
 * Manages authentication token lifecycle for Cassanova API.
 * Implements singleton pattern with memory + SessionStorage caching.
 * Handles automatic token refresh with expiry tracking.
 *
 * @module cassanova/tokenManager
 */

import axios from 'axios';
import {API_KEY, API_URL, TOKEN_REFRESH_BUFFER, TOKEN_STORAGE_KEY} from './config';

/**
 * Token Manager Class
 * Singleton instance that manages Cassanova API authentication tokens
 */
class CassanovaTokenManager {
    constructor() {
        if (CassanovaTokenManager.instance) {
            return CassanovaTokenManager.instance;
        }

        /**
         * In-memory token cache
         * @private
         */
        this.token = null;

        /**
         * Token expiry timestamp (milliseconds)
         * @private
         */
        this.expiresAt = null;

        /**
         * Promise for ongoing token refresh (prevents concurrent refreshes)
         * @private
         */
        this.refreshPromise = null;

        // Load token from SessionStorage on initialization
        this.loadFromStorage();

        CassanovaTokenManager.instance = this;
    }

    /**
     * Load token from SessionStorage
     * @private
     */
    loadFromStorage() {
        try {
            const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
            if (stored) {
                const {token, expiresAt} = JSON.parse(stored);
                this.token = token;
                this.expiresAt = expiresAt;

                if (process.env.NODE_ENV !== 'production') {
                    console.log('[TokenManager] Loaded token from storage, expires at:', new Date(expiresAt));
                }
            }
        } catch (error) {
            console.error('[TokenManager] Failed to load token from storage:', error);
            this.clearToken();
        }
    }

    /**
     * Save token to SessionStorage
     * @private
     */
    saveToStorage() {
        try {
            const data = {
                token: this.token,
                expiresAt: this.expiresAt,
            };
            sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(data));

            if (process.env.NODE_ENV !== 'production') {
                console.log('[TokenManager] Saved token to storage');
            }
        } catch (error) {
            console.error('[TokenManager] Failed to save token to storage:', error);
        }
    }

    /**
     * Check if current token is valid
     * @returns {boolean} True if token exists and is not expired (with buffer)
     * @private
     */
    isTokenValid() {
        if (!this.token || !this.expiresAt) {
            return false;
        }

        // Check if token expires within the refresh buffer
        const now = Date.now();
        const expiresWithBuffer = this.expiresAt - TOKEN_REFRESH_BUFFER;

        return now < expiresWithBuffer;
    }

    /**
     * Fetch a new token from Cassanova API
     * @returns {Promise<string>} Access token
     * @throws {Error} If token fetch fails
     * @private
     */
    async fetchNewToken() {
        try {
            if (process.env.NODE_ENV !== 'production') {
                console.log('[TokenManager] Fetching new token from API...');
            }

            const response = await axios.post(
                `${API_URL}/apikey/token`,
                {apiKey: API_KEY},
                {
                    headers: {
                        'accept': 'application/json',
                        'content-type': 'application/json',
                    },
                    timeout: 10000, // 10 second timeout
                }
            );

            const {access_token, expires_in} = response.data;

            if (!access_token || !expires_in) {
                throw new Error('Invalid token response: missing access_token or expires_in');
            }

            // Calculate expiry timestamp
            const expiresAt = Date.now() + (expires_in * 1000);

            // Update cache
            this.token = access_token;
            this.expiresAt = expiresAt;

            // Persist to storage
            this.saveToStorage();

            if (process.env.NODE_ENV !== 'production') {
                console.log('[TokenManager] Token fetched successfully, expires at:', new Date(expiresAt));
            }

            return access_token;
        } catch (error) {
            console.error('[TokenManager] Failed to fetch token:', error.message);

            // Clear invalid token
            this.clearToken();

            // Throw user-friendly error
            if (error.response) {
                throw new Error(`Authentication failed: ${error.response.status} ${error.response.statusText}`);
            } else if (error.request) {
                throw new Error('Authentication failed: Network error');
            } else {
                throw new Error(`Authentication failed: ${error.message}`);
            }
        }
    }

    /**
     * Get a valid access token
     * Returns cached token if valid, otherwise fetches a new one
     * @returns {Promise<string>} Valid access token
     * @throws {Error} If unable to obtain token
     * @public
     */
    async getToken() {
        // Return valid cached token
        if (this.isTokenValid()) {
            if (process.env.NODE_ENV !== 'production') {
                console.log('[TokenManager] Using cached token');
            }
            return this.token;
        }

        // If refresh is already in progress, wait for it
        if (this.refreshPromise) {
            if (process.env.NODE_ENV !== 'production') {
                console.log('[TokenManager] Waiting for ongoing token refresh...');
            }
            return this.refreshPromise;
        }

        // Start new refresh
        this.refreshPromise = this.fetchNewToken()
            .finally(() => {
                // Clear refresh promise when done
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    /**
     * Force refresh the token
     * @returns {Promise<string>} New access token
     * @throws {Error} If refresh fails
     * @public
     */
    async refreshToken() {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[TokenManager] Force refreshing token...');
        }

        // Clear current token
        this.clearToken();

        // Fetch new token
        return this.getToken();
    }

    /**
     * Clear token from memory and storage
     * @public
     */
    clearToken() {
        if (process.env.NODE_ENV !== 'production') {
            console.log('[TokenManager] Clearing token cache');
        }

        this.token = null;
        this.expiresAt = null;
        this.refreshPromise = null;

        try {
            sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        } catch (error) {
            console.error('[TokenManager] Failed to clear token from storage:', error);
        }
    }

    /**
     * Get token expiry information
     * @returns {Object|null} Object with expiresAt timestamp and isValid flag, or null if no token
     * @public
     */
    getTokenInfo() {
        if (!this.token) {
            return null;
        }

        return {
            expiresAt: this.expiresAt,
            isValid: this.isTokenValid(),
            expiresIn: this.expiresAt ? Math.max(0, this.expiresAt - Date.now()) : 0,
        };
    }
}

// Export singleton instance
const tokenManager = new CassanovaTokenManager();

export default tokenManager;

// Export class for testing
export {CassanovaTokenManager};
